import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { useLiveChat, ChatMessage } from '../contexts/LiveChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useLoginModal } from '../contexts/LoginModalContext';

// Inline SVG icons
const XIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const AtIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-5.656 3.656A4 4 0 0016 12zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const EmojiIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MessageIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const ReplyIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
  </svg>
);

// VIP level badge component - matching site theme
const VIPBadge: React.FC<{ level: number }> = ({ level }) => {
  const getBadgeStyle = (lvl: number) => {
    if (lvl >= 50) return { bg: 'from-neon-purple to-neon-pink', text: 'text-white' };
    if (lvl >= 30) return { bg: 'from-gold-500 to-gold-600', text: 'text-dark-900' };
    if (lvl >= 20) return { bg: 'from-neon-blue to-primary-400', text: 'text-white' };
    if (lvl >= 10) return { bg: 'from-primary-500 to-primary-600', text: 'text-white' };
    if (lvl >= 5) return { bg: 'from-dark-400 to-dark-500', text: 'text-white' };
    return { bg: 'from-accent-600 to-accent-700', text: 'text-white' };
  };

  const style = getBadgeStyle(level);

  return (
    <div className={`flex items-center justify-center px-1 rounded bg-gradient-to-r ${style.bg} ${style.text} text-[9px] font-bold min-w-[24px] h-[14px] leading-none shadow-sm`}>
      V{level}
    </div>
  );
};

// Parse message for @mentions - only highlight known usernames (e.g. @Coin 365), not the rest of the text
const parseMessage = (
  message: string,
  onMentionClick?: (username: string) => void,
  knownUsernames: string[] = []
) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  // Sort by length desc so "Coin 365" matches before "Coin"
  const sortedNames = [...knownUsernames].sort((a, b) => b.length - a.length);
  const regex = /@/g;
  let match;

  while ((match = regex.exec(message)) !== null) {
    const atIndex = match.index;
    const afterAt = message.slice(atIndex + 1);
    let matchedUsername: string | null = null;
    for (const name of sortedNames) {
      if (afterAt.startsWith(name) && (afterAt.length === name.length || !/\w/.test(afterAt[name.length]))) {
        matchedUsername = name;
        break;
      }
    }
    if (matchedUsername) {
      if (atIndex > lastIndex) {
        parts.push(message.slice(lastIndex, atIndex));
      }
      parts.push(
        <span
          key={atIndex}
          className="text-primary-400 hover:text-primary-300 cursor-pointer font-medium"
          onClick={() => onMentionClick?.(matchedUsername!)}
        >
          @{matchedUsername}
        </span>
      );
      lastIndex = atIndex + 1 + matchedUsername.length;
      regex.lastIndex = lastIndex;
    }
  }

  if (lastIndex < message.length) {
    parts.push(message.slice(lastIndex));
  }

  return parts.length > 0 ? parts : message;
};

// Parse reply format: "> @User: quoted\n\nreply" (use LAST \n\n for reply-to-reply)
const parseReplyFormat = (message: string): { quoted: string; reply: string } | null => {
  const normalized = message.replace(/\r\n/g, '\n');
  const sep = normalized.lastIndexOf('\n\n');
  if (sep === -1) return null;
  const before = normalized.slice(0, sep);
  const after = normalized.slice(sep + 2).trim();
  if (!after) return null;
  const quotedMatch = before.match(/>\s*@([^:]+):\s*([\s\S]+)/);
  if (!quotedMatch) return null;
  const quoted = `> @${quotedMatch[1].trim()}: ${quotedMatch[2].trim()}`;
  return { quoted, reply: after };
};

// Simplify quoted block to "@Username: short snippet" for cleaner display.
// For reply-to-reply: show "@Username: their reply" (e.g. "@Emanuel: good") - what they said.
const simplifyQuotedDisplay = (quoted: string, maxLen = 40): string => {
  const m = quoted.match(/^>\s*@([^:]+):\s*([\s\S]*)/);
  if (!m) return quoted;
  const username = m[1].trim();
  let content = m[2].trim();
  // If content is itself a reply ("> @X: ...\n\nactual reply"), use their reply as snippet
  const lastDoubleNewline = content.lastIndexOf('\n\n');
  if (lastDoubleNewline !== -1 && />\s*@[^:]+:/.test(content.slice(0, lastDoubleNewline))) {
    content = content.slice(lastDoubleNewline + 2).trim();
  } else {
    // Strip nested "> @User: " prefixes to get innermost message
    while (/^>\s*@[^:]+:\s*/.test(content)) {
      content = content.replace(/^>\s*@[^:]+:\s*/, '');
    }
  }
  const snippet = content.length > maxLen ? content.slice(0, maxLen) + '…' : content;
  return `@${username}: ${snippet}`;
};

// Message item component - BC.game style
const MessageItem: React.FC<{
  msg: ChatMessage;
  isOwn: boolean;
  onMention: (username: string) => void;
  onReply: (msg: ChatMessage) => void;
  knownUsernames: string[];
  t: (key: string, fallback?: string) => string;
}> = ({ msg, isOwn, onMention, onReply, knownUsernames, t }) => {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`group flex items-start gap-3 px-3 py-2 hover:bg-dark-700/50 transition-colors ${isOwn ? 'bg-primary-500/10' : ''}`}>
      <div className="flex-shrink-0 relative">
        {msg.avatar ? (
          <img
            src={msg.avatar}
            alt={msg.userName}
            className="w-10 h-10 rounded-full object-cover border-2 border-dark-600"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-dark-600 to-dark-700 flex items-center justify-center text-white text-sm font-bold border-2 border-dark-600">
            {msg.userName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2">
          <VIPBadge level={msg.userLevel} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${isOwn ? 'text-primary-400' : 'text-dark-200'}`}>
            {isOwn ? t('liveChat.me') : msg.userName}
          </span>
          <span className="text-[11px] text-dark-400">{formatTime(msg.timestamp)}</span>
        </div>
        {(() => {
          const parsed = parseReplyFormat(msg.message);
          if (parsed) {
            return (
              <div className="mt-0.5">
                <div className="border-l-4 border-primary-500 pl-3 rounded-r bg-dark-800/40 text-xs text-dark-400 break-words mb-1.5">
                  {parseMessage(simplifyQuotedDisplay(parsed.quoted), onMention, knownUsernames)}
                </div>
                <p className="text-sm text-dark-200 break-words leading-relaxed">
                  {parseMessage(parsed.reply, onMention, knownUsernames)}
                </p>
              </div>
            );
          }
          return (
            <p className="text-sm text-dark-200 break-words leading-relaxed mt-0.5">
              {parseMessage(msg.message, onMention, knownUsernames)}
            </p>
          );
        })()}
      </div>

      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onReply(msg)}
          className="p-1.5 text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded transition-colors"
          title={t('liveChat.reply', 'Reply')}
        >
          <ReplyIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => onMention(msg.userName)}
          className="p-1.5 text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded transition-colors"
          title={t('liveChat.mentionUser')}
        >
          <AtIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// User suggestion dropdown for @ mentions
const UserSuggestions: React.FC<{
  users: string[];
  onSelect: (username: string) => void;
  selectedIndex: number;
}> = ({ users, onSelect, selectedIndex }) => {
  if (users.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 bg-dark-800 border border-dark-600 rounded-lg shadow-xl max-h-40 overflow-y-auto">
      {users.map((username, index) => (
        <button
          key={username}
          onClick={() => onSelect(username)}
          className={`w-full px-3 py-2 text-left text-sm hover:bg-dark-700 transition-colors flex items-center gap-2 ${
            index === selectedIndex ? 'bg-dark-700' : ''
          }`}
        >
          <AtIcon className="w-4 h-4 text-primary-400" />
          <span className="text-dark-200">{username}</span>
        </button>
      ))}
    </div>
  );
};

// Chat rules icon
const RulesIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// Chat rules - id and icon only; title/description come from i18n
const CHAT_RULES = [
  { id: 'no-links', icon: '🔗' },
  { id: 'no-spam', icon: '🚫' },
  { id: 'no-ads', icon: '📢' },
  { id: 'be-respectful', icon: '🤝' },
  { id: 'no-sensitive', icon: '🔒' },
  { id: 'english-only', icon: '🌐' },
  { id: 'no-begging', icon: '💰' },
  { id: 'have-fun', icon: '🎉' },
];

// Validate message against chat rules; returns i18n key for error message
const validateMessage = (message: string): { valid: boolean; errorKey?: string } => {
  const trimmed = message.trim();

  if (!trimmed) return { valid: false, errorKey: 'liveChat.validation.empty' };

  const urlPattern = /(https?:\/\/|www\.|\.com|\.net|\.org|\.io|\.gg|\.xyz|\.co)/i;
  if (urlPattern.test(trimmed)) return { valid: false, errorKey: 'liveChat.validation.noLinks' };

  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  if (emailPattern.test(trimmed)) return { valid: false, errorKey: 'liveChat.validation.noEmail' };

  const phonePattern = /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/;
  if (phonePattern.test(trimmed)) return { valid: false, errorKey: 'liveChat.validation.noPhone' };

  if (trimmed.length > 500) return { valid: false, errorKey: 'liveChat.validation.tooLong' };

  const letters = trimmed.replace(/[^a-zA-Z]/g, '');
  if (letters.length > 10) {
    const uppercaseCount = (trimmed.match(/[A-Z]/g) || []).length;
    if (uppercaseCount / letters.length > 0.7) return { valid: false, errorKey: 'liveChat.validation.noCaps' };
  }

  if (/(.)\1{5,}/.test(trimmed)) return { valid: false, errorKey: 'liveChat.validation.noRepeat' };

  return { valid: true };
};

// Chat Rules Modal
const ChatRulesModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  t: (key: string, fallback?: string) => string;
}> = ({ isOpen, onClose, t }) => {
  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-dark-900/95 z-50 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-700">
        <h3 className="text-white font-semibold">{t('liveChat.rulesTitle')}</h3>
        <button
          onClick={onClose}
          className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
        >
          <XIcon />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {CHAT_RULES.map((rule) => (
          <div key={rule.id} className="flex items-start gap-3 p-3 bg-dark-800 rounded-lg">
            <span className="text-2xl">{rule.icon}</span>
            <div>
              <h4 className="text-white font-medium text-sm">{t(`liveChat.rules.${rule.id}.title`)}</h4>
              <p className="text-dark-400 text-xs mt-0.5">{t(`liveChat.rules.${rule.id}.description`)}</p>
            </div>
          </div>
        ))}
        <div className="mt-4 p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg">
          <p className="text-primary-400 text-xs">⚠️ {t('liveChat.rulesViolationWarning')}</p>
        </div>
      </div>
    </div>
  );
};

const LiveChatPanel: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, messages, onlineCount, isConnected, closeChat, sendMessage, chatError } = useLiveChat();
  const { isAuthenticated, user } = useAuth();
  const { openLoginModal } = useLoginModal();
  const [inputMessage, setInputMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const getRecentUsernames = (): string[] => {
    const usernames = new Set<string>();
    messages.forEach(msg => {
      if (msg.userName && msg.userName !== user?.name) {
        usernames.add(msg.userName);
      }
    });
    return Array.from(usernames).slice(0, 10);
  };

  // Get all known usernames: message authors + @mentioned names in content (for highlighting in received messages)
  const getAllKnownUsernames = (): string[] => {
    const usernames = new Set<string>();
    messages.forEach(msg => {
      if (msg.userName) usernames.add(msg.userName);
      const msgText = msg.message || '';
      const mentionRegex = /@([\w]+(?:\s+[\w]+)*?)(?=\s+(?:yes|no|are|is|you|the|a|an|hi|hello|there|how|what|when|where|why|can|could|will|would|do|does|did|have|has|had|ok|okay)\b|[.,!?;:]|\s*$)/gi;
      let m;
      while ((m = mentionRegex.exec(msgText)) !== null) {
        const name = m[1].trim();
        if (name) usernames.add(name);
      }
    });
    return Array.from(usernames);
  };

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && isAuthenticated && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isAuthenticated]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Handle emoji selection
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setInputMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  // Handle input change with @ mention detection
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputMessage(value);

    // Check for @ mention (allow spaces in name, e.g. @Coin 365)
    const cursorPosition = e.target.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([\w\s]*)$/);

    if (mentionMatch) {
      const searchTerm = mentionMatch[1].toLowerCase();
      const startIndex = cursorPosition - mentionMatch[0].length;
      setMentionStartIndex(startIndex);

      const allUsernames = getRecentUsernames();
      const filtered = searchTerm
        ? allUsernames.filter(u => u.toLowerCase().includes(searchTerm))
        : allUsernames;

      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Insert mention into input
  const insertMention = (username: string) => {
    if (mentionStartIndex >= 0) {
      const before = inputMessage.slice(0, mentionStartIndex);
      const cursorPos = inputRef.current?.selectionStart || inputMessage.length;
      const after = inputMessage.slice(cursorPos);
      setInputMessage(`${before}@${username} ${after}`);
    } else {
      setInputMessage(prev => `${prev}@${username} `);
    }
    setShowSuggestions(false);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  // Handle mention button click from message
  const handleMentionUser = (username: string) => {
    setInputMessage(prev => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed} @${username} ` : `@${username} `;
    });
    inputRef.current?.focus();
  };

  // Reply to a specific message - Slack/Teams style: show reply bar above input
  const handleReply = (msg: ChatMessage) => {
    console.log('handleReply', msg);
    setReplyingTo(msg);
    setInputMessage('');
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyingTo(null);
    inputRef.current?.focus();
  };

  const handleSend = () => {
    if (!inputMessage.trim()) return;
    
    // Validate message (validate only user's reply text, not the quoted part)
    const validation = validateMessage(inputMessage);
    if (!validation.valid) {
      setValidationError(validation.errorKey ? t(validation.errorKey) : t('liveChat.validation.invalid'));
      setTimeout(() => setValidationError(null), 3000);
      return;
    }
    
    // If replying, prepend quoted context (Slack/Teams style)
    const finalMessage = replyingTo
      ? `> @${replyingTo.userName}: ${replyingTo.message}\n\n${inputMessage.trim()}`
      : inputMessage.trim();
    
    sendMessage(finalMessage);
    setInputMessage('');
    setReplyingTo(null);
    setShowSuggestions(false);
    setValidationError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        insertMention(suggestions[selectedSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    } else if (e.key === 'Escape' && replyingTo) {
      e.preventDefault();
      cancelReply();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 lg:inset-auto lg:right-0 lg:top-0 lg:h-full w-full lg:w-80 xl:w-96 bg-dark-900 border-l border-dark-700 shadow-2xl z-[1001] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-700 bg-dark-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg text-white shadow-glow-green">
            <MessageIcon />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">{t('liveChat.title', 'Live Chat')}</h3>
            <div className="flex items-center gap-2 text-xs text-dark-400">
              <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-primary-500' : 'bg-red-500'}`} />
              <div className="flex items-center gap-1">
                <UsersIcon />
                <span>{onlineCount}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowRules(true)}
            className="p-2 text-dark-400 hover:text-primary-400 hover:bg-dark-700 rounded-lg transition-colors"
            title={t('liveChat.rulesTitle')}
          >
            <RulesIcon />
          </button>
          <button
            onClick={closeChat}
            className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
          >
            <XIcon />
          </button>
        </div>
      </div>
      
      {/* Chat Rules Modal */}
      <ChatRulesModal isOpen={showRules} onClose={() => setShowRules(false)} t={(key, fallback) => t(key as string, fallback as string)} />

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-dark-400 px-4">
            <div className="w-16 h-16 rounded-full bg-dark-800 flex items-center justify-center mb-4 text-primary-500">
              <MessageIcon />
            </div>
            <p className="text-sm">{t('liveChat.noMessages', 'No messages yet')}</p>
            <p className="text-xs text-dark-500 mt-1">{t('liveChat.beFirst', 'Be the first to say hello!')}</p>
          </div>
        ) : (
          <div className="py-2">
            {messages.map((msg) => (
              <MessageItem
                key={msg.id}
                msg={msg}
                isOwn={String(user?.id) === String(msg.oduserId)}
                onMention={handleMentionUser}
                onReply={handleReply}
                knownUsernames={getAllKnownUsernames()}
                t={(key, fallback) => t(key as string, fallback as string)}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="p-3 border-t border-dark-700 bg-dark-800">
        {!isAuthenticated ? (
          <button
            onClick={openLoginModal}
            className="w-full py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg font-medium text-sm hover:from-primary-400 hover:to-primary-500 transition-all shadow-glow-green"
          >
            {t('liveChat.loginToChat', 'Login to Chat')}
          </button>
        ) : (
          <div className="relative">
            {/* User suggestions */}
            <UserSuggestions
              users={suggestions}
              onSelect={insertMention}
              selectedIndex={selectedSuggestionIndex}
            />

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className="absolute bottom-full right-0 mb-2 z-50">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={Theme.DARK}
                  width={320}
                  height={350}
                  searchPlaceholder={t('liveChat.searchEmoji')}
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}

            {/* Connecting when unlocked but socket not ready */}
            {isAuthenticated && !isConnected && (
              <div className="mb-2 px-3 py-2 bg-primary-500/10 border border-primary-500/20 rounded-lg">
                <p className="text-primary-400 text-xs">{t('liveChat.connecting')}</p>
              </div>
            )}

            {/* Reply bar - Slack/Teams style */}
            {replyingTo && (
              <div className="mb-2 flex items-start gap-2 px-3 py-2 bg-dark-800 border-l-4 border-primary-500 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-primary-400 text-xs font-medium">
                    {t('liveChat.replyingTo', 'Replying to')} @{replyingTo.userName}
                  </p>
                  <p className="text-dark-400 text-xs mt-0.5 truncate">
                    {replyingTo.message}
                  </p>
                </div>
                <button
                  onClick={cancelReply}
                  className="p-1 text-dark-400 hover:text-white hover:bg-dark-700 rounded transition-colors flex-shrink-0"
                  title={t('liveChat.cancelReply', 'Cancel')}
                  aria-label={t('liveChat.cancelReply', 'Cancel')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Validation Error (client or server) */}
            {(validationError || chatError) && (
              <div className="mb-2 px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-xs">{validationError || chatError}</p>
              </div>
            )}

            <div className={`flex items-center gap-2 bg-dark-700 rounded-lg px-3 py-2 border transition-colors ${(validationError || chatError) ? 'border-red-500/50' : 'border-dark-600 focus-within:border-primary-500/50'}`}>
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={replyingTo ? t('liveChat.placeholderReply', 'Type your reply...') : t('liveChat.placeholder', 'Type a message...')}
                maxLength={500}
                className="flex-1 bg-transparent text-white text-sm placeholder-dark-400 focus:outline-none"
              />
              <button
                onClick={() => setShowEmojiPicker(prev => !prev)}
                className={`p-1.5 transition-colors ${showEmojiPicker ? 'text-accent-400' : 'text-dark-400 hover:text-accent-400'}`}
                title={t('liveChat.emoji')}
              >
                <EmojiIcon />
              </button>
              <button
                onClick={() => {
                  setInputMessage(prev => prev + '@');
                  inputRef.current?.focus();
                }}
                className="p-1.5 text-dark-400 hover:text-primary-400 transition-colors"
                title={t('liveChat.mentionUser')}
              >
                <AtIcon className="w-5 h-5" />
              </button>
              <button
                onClick={handleSend}
                disabled={!inputMessage.trim() || !isConnected}
                title={!isConnected ? t('liveChat.connectingShort') : t('liveChat.send')}
                className="p-1.5 text-dark-400 hover:text-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SendIcon />
              </button>
            </div>
            <p className="text-[10px] text-dark-500 mt-1 px-1">
              {inputMessage.length}/500
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveChatPanel;
